import React, { useEffect, useMemo, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { GlobalResponse, Slug } from '../../../models/domain/interfaces';
import Banner, { BannerProps } from '../../../components/banner/Banner';
import { StyledContentWrapper } from './CardTopNews.style';
import { Newsa as News } from '../../../models/auto-generated-strapi-types/types/Newsa';
import BannerVideo, {
  BannerVideoProps,
} from '../../../components/banner-video/BannerVideo';

interface CardTopNewsProps {
  news: {
    data: News;
  };
  breadCrumb: {
    links: {
      href: string;
      name: string;
      disabled?: boolean;
      color?: string;
    }[];
    pageActive: {
      name: string;
      color?: string;
    };
  };
  forceHideDescription?: boolean;
  forceHideButton?: boolean;
  forceHideBreadcrumb?: boolean;
  headerDescriptionPosition?: 'default' | 'under' | 'hidden';
  forceHideTitle?: boolean;
  forceDescriptionToBeWhite?: boolean;
  forceDescriptionToBeBlack?: boolean;
  headerCoverResize?: 'default_cover' | 'contain' | 'hidden';
  newsAndEventsMerged?: boolean;
  forceTextInsideImageAtSmallScreenForDefaultPosition?: boolean;
}

const CardTopNews: React.FC<any> = ({
  data,
  translations,
  isPrerenderRequest,
}: {
  data: CardTopNewsProps;
  translations: any;
  isPrerenderRequest: boolean;
}) => {
  const { news, breadCrumb, newsAndEventsMerged } = data ?? {};
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('sm'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  const image = useMemo(() => {
    if (data?.news?.data) {
      const { coverImage, mobileCoverImage } = data?.news?.data?.attributes;
      return typeof window === 'undefined' || isDesktop
        ? coverImage
        : mobileCoverImage?.data
          ? mobileCoverImage
          : coverImage;
    }
  }, [data.news.data, isDesktop]);

  const video = useMemo(() => {
    if (data?.news?.data) {
      const { video, mobileVideo } = data?.news?.data?.attributes;
      return typeof window === 'undefined' || isDesktop
        ? video
        : mobileVideo?.data
          ? mobileVideo
          : video;
    }
  }, [data?.news?.data, isDesktop]);

  //card per news o evento in primo piano
  if (news.data) {
    const {
      title,
      description,
      overlayColor,
      slug,
      videoAutoPlay,
      videoControls,
      videoMuted,
      videoLoop,
    } = news.data.attributes;
    const {
      forceHideDescription,
      forceHideButton,
      forceHideBreadcrumb,
      headerDescriptionPosition,
      forceHideTitle,
      forceDescriptionToBeWhite,
      forceDescriptionToBeBlack,
      headerCoverResize,
      forceTextInsideImageAtSmallScreenForDefaultPosition,
    } = data;

    const linkNews = `/${
      newsAndEventsMerged
        ? translations?.urls?.newsAndEvents
        : translations?.urls?.newsEntryPage
    }/${translations?.urls?.news}/${slug}`;

    let banner: BannerProps | BannerVideoProps = {
      title,
      description,
      image,
      button: {
        text: translations?.pages?.home?.newsEventsSlider?.goToNews,
        url: linkNews,
      },
      overlayColor,
      breadCrumb,
      textLineLimit: false,
      forceHideDescription,
      forceHideButton,
      forceHideBreadcrumb,
      headerDescriptionPosition,
      forceHideTitle,
      forceDescriptionToBeWhite,
      forceDescriptionToBeBlack,
      forceTextInsideImageAtSmallScreenForDefaultPosition,
      headerCoverResize,
      isPrerenderRequest: isPrerenderRequest,
    };

    if (video?.data) {
      banner = {
        ...banner,
        video,
        videoAutoPlay,
        videoControls,
        videoLoop,
        videoMuted,
      };
      return (
        <StyledContentWrapper>
          <BannerVideo {...banner}></BannerVideo>
        </StyledContentWrapper>
      );
    } else {
      banner = { ...banner, image };
      return (
        <StyledContentWrapper>
          <Banner {...banner}></Banner>
        </StyledContentWrapper>
      );
    }
  } else {
    //TODO
    //Ritornare ultima notizia inserita se l'utente non inserisce nessuna news nel cms,
    //campo relazione nel cms non permette campo obbligatorio
    return <div></div>;
  }
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  section.newsAndEventsMerged = globalLocale?.attributes?.newsAndEventsMerged;
};

export default CardTopNews;
